import { PublicClientApplication, type Configuration, type RedirectRequest } from '@azure/msal-browser';

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID as string;
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string;
const redirectUri = (import.meta.env.VITE_ENTRA_REDIRECT_URI as string) ?? window.location.origin;
export const apiScope = import.meta.env.VITE_API_SCOPE as string;

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    // localStorage, not sessionStorage: sessionStorage is per-tab, so closing the
    // tab or force-quitting Safari threw away the token cache and forced a fresh
    // interactive sign-in. Worse for the field, that also meant queued offline
    // drafts hit 'no_account' on every sync attempt and flipped to syncBlocked,
    // needing a manual Retry for what a sign-in fixed. Tokens now survive an app
    // restart and are shared across tabs.
    //
    // The trade is tokens at rest in localStorage (readable by injected script)
    // rather than dying with the tab. Accepted deliberately: this is a
    // first-party app on managed devices, and the availability cost of the old
    // behaviour was being paid daily in the field.
    //
    // NOTE: this does NOT move the interaction-status key — MSAL keeps that in
    // temporaryCacheLocation, which still defaults to sessionStorage, so the
    // 'interaction_in_progress' self-heal in SignIn.tsx keeps working.
    cacheLocation: 'localStorage',
    // Keep the redirect's PKCE/nonce state in a cookie as well as web storage.
    // Safari's ITP can drop the storage copy across the round-trip to Microsoft,
    // which makes the returning redirect fail to match its request — the field
    // iPads are exactly the affected configuration.
    storeAuthStateInCookie: true,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Filter undefined so MSAL doesn't crash with "Cannot read properties of
// undefined (reading 'trim')" when VITE_API_SCOPE is unset (e.g. dev worktree
// without a .env file).
export const loginRequest: RedirectRequest = {
  scopes: [apiScope, 'openid', 'profile', 'email'].filter(
    (s): s is string => typeof s === 'string' && s.length > 0,
  ),
};

export const apiTokenRequest = {
  scopes: [apiScope].filter((s): s is string => typeof s === 'string' && s.length > 0),
};

export const isMsalConfigured = Boolean(clientId && tenantId && apiScope);
